import { kit } from './strings'
import './LabelChip.css'

/** How many tints the palette holds. See LabelChip.css. */
const TONES = 8

/**
 * The tint a label always wears, derived from its own text.
 *
 * Deterministic on purpose: `المدراء` is the same colour in every row of every
 * table, so the eye learns the department once instead of re-reading it. A
 * random or index-based colour would repaint the same name on every sort.
 */
const toneOf = (text = '') => {
  let hash = 0
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash * 31 + text.charCodeAt(index)) >>> 0
  }
  return hash % TONES
}

/**
 * LabelChip — a soft pill for a name that CLASSIFIES rather than reports state.
 *
 * StatusBadge answers "how is this row doing"; this answers "which one is it" —
 * departments, groups, tags. It carries no dot and no single accent: the tint
 * comes from the label's own text, so a user in four departments reads as four
 * distinguishable chips instead of four identical emerald ones.
 *
 * @param {number} [tone]  override the hashed tint — how `LabelChips` keeps two
 *   names that happen to collide apart within one cell
 * @param {string} [title]  native tooltip, for a chip standing in for others
 */
export default function LabelChip({ children, tone, title }) {
  const index = tone ?? toneOf(typeof children === 'string' ? children : '')

  return (
    <span className={`label-chip label-chip--t${index}`} title={title}>
      <bdi>{children}</bdi>
    </span>
  )
}

/**
 * A SET of labels shown together — the departments a user belongs to, in one
 * table cell.
 *
 * Each name keeps its own hashed tint; one that collides with a tint already
 * used in the same cell steps to the next free one, because the whole point of
 * colouring them is telling them apart. Past `max` the rest fold into a single
 * count that names them on hover, so a cell stays one line wide no matter how
 * many memberships sit behind it.
 *
 * @param {string[]} names
 * @param {number} [max]  how many to draw before folding the rest into a count
 */
export function LabelChips({ names = [], max = 2 }) {
  const used = new Set()
  const shown = names.slice(0, max).map((name) => {
    let tone = toneOf(name)
    while (used.has(tone) && used.size < TONES) tone = (tone + 1) % TONES
    used.add(tone)
    return { name, tone }
  })
  const hidden = names.slice(max)

  return (
    <span className="label-chips">
      {shown.map(({ name, tone }) => (
        <LabelChip key={name} tone={tone}>
          {name}
        </LabelChip>
      ))}
      {/* bdi, or the neutral "+" flips to the far side of the digit in RTL. */}
      {hidden.length > 0 && (
        <span className="label-chips__more" title={hidden.join(kit.listSep)}>
          <bdi>+{hidden.length}</bdi>
        </span>
      )}
    </span>
  )
}
